import React, { FunctionComponent } from 'react';
import { translate } from 'react-i18next';
import { View } from 'react-native';
import { WorkoutSettingsBase } from 'fitbeat.models';
import { Button, ButtonSize, ButtonTypes } from '../../../components';
import { Orientation, Orientations } from '../../../HomeWorkouts';
import Theme, { ThemeType } from '../../../theme';
import { defaultScale, i18n } from '../../../utils';
import { PersonaliseSetupWarning, themeStyleMapper } from './PersonaliseWorkoutSetup';
import { PersonaliseSetupScreens } from './types';
import { isDesktop } from '../../../utils/ScreenSizes';

interface IProps {
  themeType: ThemeType;
  orientation: Orientation;
  currentPersonaliseSetupScreen: PersonaliseSetupScreens | null;
  workoutSettingsIsUpdating: any;
  personaliseSetupWarning: PersonaliseSetupWarning | null;
  settingsChanged: boolean;
  workoutDeviceSetup: boolean;
  workoutSettings: WorkoutSettingsBase;
  goBack(): void;
  saveAndClose(): void;
  closeSetupAndResetOverviewScreen(): void;
  clearWarning(): void;
  openPersonaliseSetupScreen(screen: PersonaliseSetupScreens, workoutSettings: WorkoutSettingsBase): void;
}

function BackButton(props) {
  if (
    props.orientation === 'PORTRAIT'
    && props.currentPersonaliseSetupScreen === PersonaliseSetupScreens.Overview
    && !props.workoutDeviceSetup
  ) {
    return (
      <Button
        fontColor={Theme.WHITE}
        buttonType={ButtonTypes.NORMAL}
        {...props}
      />
    );
  }
  if (props.currentPersonaliseSetupScreen !== PersonaliseSetupScreens.Overview) {
    return (
      <Button
        fontColor={Theme.PRIMARY}
        buttonType={ButtonTypes.OUTLINE}
        {...props}
      />
    );
  }
  return null;
}

const PersonaliseWorkoutFooterComponent: FunctionComponent<IProps> = ({
  themeType = ThemeType.dark,
  orientation,
  workoutSettingsIsUpdating,
  currentPersonaliseSetupScreen,
  personaliseSetupWarning,
  settingsChanged,
  goBack,
  saveAndClose,
  closeSetupAndResetOverviewScreen,
  clearWarning,
  openPersonaliseSetupScreen,
  workoutDeviceSetup = false,
  workoutSettings,
}) => {
  const themeStyle = themeStyleMapper[themeType];
  const orientationStyle = orientationStyleMapper[orientation];

  const hasWarning = personaliseSetupWarning === PersonaliseSetupWarning.CustomiseWarning
    || personaliseSetupWarning === PersonaliseSetupWarning.BodyGroupEquipmentWarning
    || personaliseSetupWarning === PersonaliseSetupWarning.EquipmentWarning
    || personaliseSetupWarning === PersonaliseSetupWarning.BagContactBalanceWarning
    || personaliseSetupWarning === PersonaliseSetupWarning.BodyGroupBalanceWarning;

  return (
    <View style={[orientationStyle.containerWrapper]}>
      {!hasWarning ? (
        <View style={orientationStyle.container}>
          <BackButton
            orientation={orientation}
            currentPersonaliseSetupScreen={currentPersonaliseSetupScreen}
            size={ButtonSize.MEDIUM}
            title={i18n.t('bookings:back')}
            onPress={() => goBack()}
            containerStyle={orientationStyle.backContainer}
            workoutDeviceSetup={workoutDeviceSetup}
          />
          {currentPersonaliseSetupScreen !== PersonaliseSetupScreens.Overview && (
            <Button
              fontColor={Theme.WHITE}
              buttonType={ButtonTypes.NORMAL}
              size={ButtonSize.MEDIUM}
              disabled={!settingsChanged}
              title={i18n.t('bookings:Change')}
              onPress={() => saveAndClose()}
              isLoading={workoutSettingsIsUpdating}
              containerStyle={[orientationStyle.nextContainer, {
                borderColor: (!settingsChanged || workoutSettingsIsUpdating) ? Theme.GREY_MEDIUM_LIGHT : Theme.PRIMARY,
                backgroundColor: (!settingsChanged || workoutSettingsIsUpdating)
                  ? Theme.GREY_MEDIUM_LIGHT
                  : Theme.PRIMARY,
              }]}
              overrideFontStyles={{ color: settingsChanged ? Theme.WHITE : Theme.GREY_DARK_MEDIUM }}
            />
          )}
        </View>
      ) : (
        <View style={[orientationStyle.footer, themeStyle.container]}>
          {personaliseSetupWarning === PersonaliseSetupWarning.CustomiseWarning
            && (
            <Button
              size={ButtonSize.MEDIUM}
              title={i18n.t('bookings:customise')}
              onPress={() => clearWarning()}
              buttonType={ButtonTypes.OUTLINE}
              containerStyle={orientationStyle.leftButton}
            />
            )}
          <Button
            size={ButtonSize.MEDIUM}
            title={personaliseSetupWarning === PersonaliseSetupWarning.CustomiseWarning
              ? i18n.t('bookings:dontCustomise') : i18n.t('bookings:continue')}
            onPress={() => (personaliseSetupWarning === PersonaliseSetupWarning.CustomiseWarning
              ? closeSetupAndResetOverviewScreen()
              : openPersonaliseSetupScreen(PersonaliseSetupScreens.Overview, workoutSettings))}
            fontColor={Theme.WHITE}
            containerStyle={orientationStyle.rightButton}
          />
        </View>
      )}
    </View>
  );
};

const orientationStyleMapper = {
  [Orientations.portrait]: {
    containerWrapper: {
      width: '100%',
    },
    container: {
      width: '100%',
      flexDirection: 'row' as const,
      justifyContent: 'space-between' as const,
      padding: 15,
    },
    backContainer: {
      flex: 1,
      alignSelf: 'stretch',
    },
    nextContainer: {
      flex: 1,
      marginLeft: 5,
    },
    footer: {
      flexDirection: 'row' as const,
      justifyContent: 'space-between' as const,
      padding: 15,
      width: '100%',
    },
    leftButton: {
      flex: 1,
      flexBasis: 0,
      marginRight: 15,
    },
    rightButton: {
      flex: 1,
      flexBasis: 0,
    },
  },
  [Orientations.landscape]: {
    containerWrapper: {
      width: '100%',
      alignSelf: 'center' as const,
      position: 'absolute' as const,
      bottom: 0,
      marginBottom: defaultScale(-20),
    },
    container: {
      width: '100%',
      flexDirection: 'row' as const,
      justifyContent: 'space-between' as const,
    },
    backContainer: {
      flex: 1,
      marginRight: defaultScale(5),
    },
    nextContainer: {
      flex: 1,
      marginLeft: defaultScale(5),
    },
    footer: {
      maxWidth: isDesktop() ? defaultScale(600) : '100%',
      flexDirection: 'row' as const,
      padding: defaultScale(15),
    },
    leftButton: {
      flex: 1,
      marginRight: defaultScale(5),
    },
    rightButton: {
      flex: 1,
      marginLeft: defaultScale(5),
    },
  },
};

export const PersonaliseWorkoutFooter = translate(['bookings'])(PersonaliseWorkoutFooterComponent);
